import { useEffect, useMemo } from "react";
import { Division, Flex, Heading, Skeleton, useToast } from "@ir/ui";
import { Outlet, useNavigate } from "react-router";
import ErrorBoundary from "@src/presentation/components/common/ErrorBoundary";
import {
  PageContent,
  PageHeader,
} from "@src/presentation/components/Layout/common";
import { type BreadcrumbItem } from "@src/presentation/components/common";
import { ConfigBrandProvider, useConfigBrand } from "./ConfigBrandContext";
import { ConfigOrgBrandSelector } from "./utils/ConfigOrgBrandSelector";
import { ConfigSubNav } from "./utils/ConfigSubNav";
import {
  getConfigSectionLabel,
  isConfigSection,
} from "./configNav.config";
import "./Config.scss";

const ConfigLayoutContent = () => {
  const navigate = useNavigate();
  const { error: errorToast } = useToast();
  const {
    organizationKey,
    brandKey,
    brand,
    section,
    isInvalidBrandSelection,
    isLookupReady,
    isLookupLoading,
  } = useConfigBrand();

  useEffect(() => {
    if (!isLookupReady || !isInvalidBrandSelection) {
      return;
    }
    errorToast({
      content: "Selected organization or brand was not found.",
    });
    navigate("/configuration", { replace: true });
  }, [errorToast, isInvalidBrandSelection, isLookupReady, navigate]);

  const showSubNav =
    Boolean(organizationKey && brandKey) && !isInvalidBrandSelection;

  const isListSection =
    Boolean(section) && isConfigSection(section ?? undefined);

  const breadcrumbItems = useMemo((): BreadcrumbItem[] => {
    const root: BreadcrumbItem = {
      key: "config-root",
      title: "Configuration",
      path: organizationKey && brandKey ? "/configuration" : undefined,
    };

    if (!organizationKey || !brandKey) {
      return [root];
    }

    const brandLabel = brand?.brandDisplayName ?? brand?.brandName ?? brandKey;

    const brandTitle =
      isLookupLoading && !brand ? (
        <Skeleton height={16} width={160} animation="wave" variant="rounded" />
      ) : (
        `${brandLabel}`
      );

    const brandCrumb: BreadcrumbItem = {
      key: `config-brand-${organizationKey}-${brandKey}`,
      title: brandTitle,
      path: section ? `/configuration/${organizationKey}/${brandKey}` : undefined,
    };

    if (!section) {
      return [root, brandCrumb];
    }

    return [
      root,
      brandCrumb,
      {
        key: `config-section-${section}`,
        title: getConfigSectionLabel(section) ?? section,
      },
    ];
  }, [organizationKey, brandKey, brand, section, isLookupLoading]);

  return (
    <>
      <PageHeader breadcrumb={{ items: breadcrumbItems }}>
        <Flex align="center" gap={1} justifyContent="space-between">
          <Heading level={5} className="text-grey-700 font-bold m-0">
            Configuration
          </Heading>
          <ConfigOrgBrandSelector />
        </Flex>
      </PageHeader>
      <PageContent
        className={`p-4 config-page-content${showSubNav && isListSection ? " config-page-content--list" : ""}`}
      >
        {showSubNav ? (
          <Division className="config-layout">
            <Division className="config-layout__sidebar">
              <ConfigSubNav />
            </Division>
            <Division className="config-layout__content">
              <Outlet />
            </Division>
          </Division>
        ) : (
          <Outlet />
        )}
      </PageContent>
    </>
  );
};

export const ConfigLayout = () => (
  <ErrorBoundary>
    <ConfigBrandProvider>
      <ConfigLayoutContent />
    </ConfigBrandProvider>
  </ErrorBoundary>
);
